import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Check, Phone, Star } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import QuoteForm from "@/components/site/QuoteForm";
import StickyCallBar from "@/components/site/StickyCallBar";
import { SERVICES, getService } from "@/lib/services";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [slug]);

  if (!service) return <Navigate to="/" replace />;

  const related = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 3);

  return (
    <div data-testid={`service-page-${service.slug}`} className="bg-[var(--rr-bg)] text-[var(--rr-text)] font-body">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[70svh] w-full overflow-hidden grain">
        <div className="absolute inset-0">
          <img
            src={service.image}
            alt={service.name}
            className={`h-full w-full object-cover scale-105 saturate-[1.15] contrast-[1.05] ${
              service.slug === "lawn-care" ? "object-[center_55%]" : "object-center"
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[var(--rr-bg)] via-[var(--rr-bg)]/75 to-[var(--rr-bg)]/20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--rr-bg)]/40 via-transparent to-[var(--rr-bg)]" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-10 lg:px-12 pt-32 md:pt-40 pb-20">
          <Link
            to="/"
            data-testid="back-to-home"
            className="inline-flex items-center gap-2 text-sm text-[var(--rr-text-muted)] hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to all services
          </Link>

          <div className="max-w-3xl mt-8 reveal">
            <p className="overline">Rapid Response · Service</p>
            <h1 className="mt-5 font-display font-semibold text-white text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tighter">
              {service.name}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[var(--rr-text-muted)] leading-relaxed">
              {service.tagline}
            </p>
          </div>
        </div>
      </section>

      {/* Overview + What's included */}
      <section className="relative py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5 reveal">
            {service.overviewImage ? (
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/5">
                <img
                  src={service.overviewImage}
                  alt={`${service.name} — additional work`}
                  className="absolute inset-0 h-full w-full object-cover"
                  style={service.overviewRotate ? { transform: `rotate(${service.overviewRotate}deg) scale(1.25)` } : undefined}
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--rr-bg)]/40 to-transparent" />
              </div>
            ) : (
              <div className="relative aspect-[4/5] rounded-2xl border border-dashed border-white/10 bg-[var(--rr-surface)] flex items-center justify-center">
                <div className="text-center px-6">
                  <p className="overline">Overview</p>
                  <p className="mt-3 text-sm text-[var(--rr-text-muted)]">
                    Image coming soon
                  </p>
                </div>
              </div>
            )}
          </div>
          <div className="lg:col-span-7 reveal">
            <p className="text-lg leading-relaxed text-[var(--rr-text-muted)]">
              {service.description}
            </p>

            <h3 className="mt-12 font-display text-xl font-semibold text-white">Services</h3>
            {service.bullets.some((b) => typeof b === "object") ? (
              <div className="mt-5 space-y-8">
                {service.bullets.map((b, gi) =>
                  typeof b === "object" ? (
                    <div key={b.heading || gi}>
                      <h4 className="font-display text-lg font-bold text-white">{b.heading}</h4>
                      <ul className="mt-3 space-y-2">
                        {b.items.map((it, i) => (
                          <li key={i} className="flex items-start gap-3 text-[15px] text-white/90">
                            <span className="mt-1 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[var(--rr-primary)]/20 text-[var(--rr-accent)]">
                              <Check className="h-3 w-3" strokeWidth={3} />
                            </span>
                            <span>
                              <span className="font-semibold text-white mr-1">{i + 1}.</span>
                              {it}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div key={gi} className="flex items-start gap-3 text-[15px] text-white/90">
                      <span className="mt-1 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[var(--rr-primary)]/20 text-[var(--rr-accent)]">
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                      {b}
                    </div>
                  )
                )}
              </div>
            ) : (
              <ul className="mt-5 grid sm:grid-cols-2 gap-3">
                {service.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[15px] text-white/90">
                    <span className="mt-1 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[var(--rr-primary)]/20 text-[var(--rr-accent)]">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      {/* Gallery — 4 photo slots */}
      <section data-testid="service-gallery-section" className="relative pb-24 md:pb-32">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          <div className="mb-10 reveal">
            <p className="overline">Recent work</p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold tracking-tighter text-white">
              A closer look.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => {
              const photo = service.photos && service.photos[i];
              const src = typeof photo === "string" ? photo : photo?.src;
              const rotate = typeof photo === "object" ? photo?.rotate : null;
              return src ? (
                <div
                  key={i}
                  data-testid={`service-photo-${i}`}
                  className="reveal relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/5 card-lift"
                  style={{ transitionDelay: `${i * 70}ms` }}
                >
                  <img
                    src={src}
                    alt={`${service.name} — photo ${i + 1}`}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover"
                    style={rotate ? { transform: `rotate(${rotate}deg) scale(1.25)` } : undefined}
                  />
                </div>
              ) : (
                <div
                  key={i}
                  data-testid={`service-photo-placeholder-${i}`}
                  className="reveal relative aspect-[4/5] rounded-2xl border border-dashed border-white/10 bg-[var(--rr-surface)] flex items-center justify-center"
                  style={{ transitionDelay: `${i * 70}ms` }}
                >
                  <div className="text-center px-6">
                    <p className="overline">Photo {i + 1}</p>
                    <p className="mt-3 text-sm text-[var(--rr-text-muted)]">
                      Image coming soon
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="relative py-24 md:py-32 bg-[var(--rr-bg-deep)] border-y border-white/5">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5 reveal">
              <p className="overline">Our process</p>
              <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold tracking-tighter text-white">
                Simple, honest,
                <br />
                <span className="text-[var(--rr-accent)]">done right.</span>
              </h2>
            </div>
          </div>

          <ol className="grid md:grid-cols-2 gap-4">
            {service.process.map((step, i) => (
              <li
                key={step}
                className="reveal card-lift relative bg-[var(--rr-surface)] border border-white/5 rounded-2xl p-6"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="font-display text-3xl font-semibold text-[var(--rr-primary)]">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <p className="mt-3 text-[15px] leading-relaxed text-white/90">{step}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Trust strip */}
      <section className="relative py-14 bg-[var(--rr-surface)] border-b border-white/5">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-5 w-5 fill-[var(--rr-accent)] text-[var(--rr-accent)]" />
              ))}
            </div>
            <span className="text-sm text-white">
              <span className="font-semibold">5.0</span>{" "}
              <span className="text-[var(--rr-text-muted)]">across 11 verified Google reviews</span>
            </span>
          </div>
          <a
            href="#quote"
            className="inline-flex items-center px-6 h-11 rounded-full bg-[var(--rr-primary)] hover:bg-[var(--rr-primary-hover)] text-white font-semibold text-sm transition-colors"
          >
            Get a free {service.name.toLowerCase()} quote
          </a>
        </div>
      </section>

      {/* Related services */}
      <section className="relative py-24 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          <div className="flex items-end justify-between mb-10 reveal">
            <div>
              <p className="overline">Also offered</p>
              <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold tracking-tighter text-white">
                Related services
              </h2>
            </div>
            <Link
              to="/"
              className="hidden md:inline-flex items-center gap-2 text-sm text-[var(--rr-text-muted)] hover:text-white transition-colors"
            >
              View all →
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {related.map((s, i) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`related-service-${s.slug}`}
                className="reveal group relative block overflow-hidden rounded-2xl bg-[var(--rr-surface)] border border-white/5 aspect-[4/3] card-lift"
                style={{ transitionDelay: `${i * 70}ms` }}
              >
                <img src={s.image} alt={s.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--rr-bg)] via-[var(--rr-bg)]/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <div className="font-display text-xl font-semibold text-white tracking-tight">{s.name}</div>
                  <div className="text-[13px] text-white/70 mt-1">{s.tagline}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Quote form */}
      <QuoteForm defaultService={service.name} />

      <Footer />
      <StickyCallBar />
    </div>
  );
}
